import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Flex,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  CheckIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  UserGroupIcon,
} from '@heroicons/react/outline';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import {
  FundraisingStatus,
  useGetCompanyQuery,
  useGetFundraisingQuery,
} from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency, formatNumberPercentage } from '@blockpulse3/helpers';
import { BoxIcon, ErrorQueryCard } from '@blockpulse3/ui/commons';

import { privateSubscriptionPeriodOptions } from './schema';

type Props = unknown;

export function NewPrivateFundraisingSummary(): JSX.Element {
  const i18nFundraisingSummaryStep = useTranslations('FundraisingSummaryStep');
  const i18nFundraisingSummaryInformations = useTranslations('FundraisingSummaryInformations');

  const navigate = useNavigate();

  const { fundraisingId = '', companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const { data, loading, error } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
    skip: !fundraisingId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.fundraising.status !== FundraisingStatus.DRAFT) {
        navigate(routes.workspace.fmt(companyId));
      }
    },
    onError: () => {
      navigate(routes.workspace.fmt(companyId));
    },
  });

  const fundraising = data?.fundraising;
  const subscribersCount = fundraising?.subscriptionCount || 0;

  if (loading) {
    return (
      <HStack px="6" spacing="4">
        <Skeleton h="250px" w="50%" />
        <Skeleton h="250px" w="50%" />
      </HStack>
    );
  }

  if (error) {
    return (
      <HStack px="6" spacing="4">
        <ErrorQueryCard h="250px" w="full" />
      </HStack>
    );
  }

  const handleStepCancel = (): void => {
    if (company) {
      navigate(routes.workspace.fundraising.private.subscribers.fmt(company.id, fundraisingId));
    }
  };

  const handleFundraisingSubmit = (): void => {
    if (company) {
      navigate(routes.workspace.fundraising.fmt(company.id, fundraisingId));
    }
  };

  return (
    <Card variant="divider-top">
      <CardHeader as={Stack} spacing="2">
        <Heading size="lg">{i18nFundraisingSummaryStep('Title')}</Heading>
        <Text color="gray.600" fontSize="lg">
          {i18nFundraisingSummaryStep('Description')}
        </Text>
      </CardHeader>
      <Divider />
      <CardBody as={Stack} spacing="5">
        <Stack direction={{ base: 'column', md: 'row' }} spacing="2">
          <HStack
            alignItems="flex-start"
            bg="gray.50"
            p="8"
            rounded="md"
            spacing="4"
            w={{ base: 'full', md: '50%' }}
          >
            <BoxIcon icon={UserGroupIcon} iconBoxSize="24px" />
            <Stack spacing="4">
              <Stack spacing="0">
                <Text fontSize="lg" fontWeight="600">
                  {fundraising?.name}
                </Text>
                <Text lineHeight="1">{i18nFundraisingSummaryInformations('NameSubLabel')}</Text>
              </Stack>
              <Stack spacing="0">
                <Text fontSize="lg" fontWeight="600">
                  {i18nFundraisingSummaryInformations('SubscriberTitle', {
                    len: subscribersCount,
                  })}
                </Text>
                <Text lineHeight="1">
                  {i18nFundraisingSummaryInformations('SubscriberSubLabel')}
                </Text>
              </Stack>
              <Stack spacing="0">
                <Text fontSize="lg" fontWeight="600">
                  {fundraising?.subscriptionPeriod
                    ? (
                        privateSubscriptionPeriodOptions.find(
                          (opt) => opt.value === fundraising.subscriptionPeriod,
                        ) || {}
                      ).label
                    : ''}
                </Text>
                <Text lineHeight="1">{i18nFundraisingSummaryInformations('PeriodSubLabel')}</Text>
              </Stack>
            </Stack>
          </HStack>
          <HStack
            alignItems="flex-start"
            bg="gray.50"
            p="8"
            rounded="md"
            spacing="4"
            w={{ base: 'full', md: '50%' }}
          >
            <BoxIcon icon={CurrencyDollarIcon} iconBoxSize="24px" />
            <Stack spacing="4">
              <Stack spacing="0">
                <Text fontSize="lg" fontWeight="600">
                  {formatNumberCurrency(fundraising?.hardCap || '')}
                </Text>
                <Text lineHeight="1">{i18nFundraisingSummaryInformations('AmountSubLabel')}</Text>
              </Stack>
              <Stack spacing="0">
                <Text fontSize="lg" fontWeight="600">
                  {formatNumberCurrency(fundraising?.data?.valuation || '')}
                </Text>
                <Text lineHeight="1">
                  {i18nFundraisingSummaryInformations('PreAmountSubLabel')}
                </Text>
              </Stack>
              <Stack spacing="0">
                <Text fontSize="lg" fontWeight="600">
                  {formatNumberPercentage(fundraising?.data?.capitalHeldByNewSubscriptions || '')}
                </Text>
                <Text lineHeight="1">
                  {i18nFundraisingSummaryInformations('PercentageSubLabel')}
                </Text>
              </Stack>
            </Stack>
          </HStack>
        </Stack>
        <Stack spacing="6">
          <Stack spacing="4">
            <Heading size="md">{i18nFundraisingSummaryStep('Documents')}</Heading>
            <SimpleGrid gap="4" minChildWidth="300px">
              <HStack>
                <Flex
                  alignItems="center"
                  bg="gray.50"
                  borderRadius="full"
                  boxSize="32px"
                  justifyContent="center"
                >
                  <Icon as={DocumentTextIcon} boxSize="24px" color="gray.500" />
                </Flex>
                <Text color="gray.600" fontWeight="medium">
                  {i18nFundraisingSummaryStep('Record')}
                </Text>
              </HStack>
              <HStack>
                <Flex
                  alignItems="center"
                  bg="gray.50"
                  borderRadius="full"
                  boxSize="32px"
                  justifyContent="center"
                >
                  <Icon as={DocumentTextIcon} boxSize="24px" color="gray.500" />
                </Flex>
                <Text color="gray.600" fontWeight="medium">
                  {i18nFundraisingSummaryStep('SubscriptionForm')}
                </Text>
              </HStack>
              <HStack>
                <Flex
                  alignItems="center"
                  bg="gray.50"
                  borderRadius="full"
                  boxSize="32px"
                  justifyContent="center"
                >
                  <Icon as={DocumentTextIcon} boxSize="24px" color="gray.500" />
                </Flex>
                <Text color="gray.600" fontWeight="medium">
                  {i18nFundraisingSummaryStep('FindingReport')}
                </Text>
              </HStack>
            </SimpleGrid>
          </Stack>
          <Stack spacing="4">
            <Heading size="md">{i18nFundraisingSummaryStep('IncludedServices')}</Heading>
            <SimpleGrid gap="4" minChildWidth="300px">
              <HStack>
                <Icon as={CheckIcon} boxSize="24px" color="green.500" />
                <Text color="gray.600" fontWeight="medium">
                  {i18nFundraisingSummaryStep('ElectronicSignature')}
                </Text>
              </HStack>
              <HStack>
                <Icon as={CheckIcon} boxSize="24px" color="green.500" />
                <Text color="gray.600" fontWeight="medium">
                  {i18nFundraisingSummaryStep('ManagementFinancialFlows')}
                </Text>
              </HStack>
              <HStack>
                <Icon as={CheckIcon} boxSize="24px" color="green.500" />
                <Text color="gray.600" fontWeight="medium">
                  {i18nFundraisingSummaryStep('AutomaticUpdate')}
                </Text>
              </HStack>
              <HStack>
                <Icon as={CheckIcon} boxSize="24px" color="green.500" />
                <Text color="gray.600" fontWeight="medium">
                  {i18nFundraisingSummaryStep('ArchivingAndCentralization')}
                </Text>
              </HStack>
              <HStack>
                <Icon as={CheckIcon} boxSize="24px" color="green.500" />
                <Text color="gray.600" fontWeight="medium">
                  {i18nFundraisingSummaryStep('AutomatedTitleDistribution')}
                </Text>
              </HStack>
              <HStack>
                <Icon as={CheckIcon} boxSize="24px" color="green.500" />
                <Text color="gray.600" fontWeight="medium">
                  {i18nFundraisingSummaryStep('UpdateSecuritiesRegister')}
                </Text>
              </HStack>
            </SimpleGrid>
          </Stack>
        </Stack>
      </CardBody>
      <CardFooter as={Stack} direction={{ base: 'column', md: 'row' }} spacing="4">
        <Button variant="secondary" w="full" onClick={handleStepCancel}>
          {i18nFundraisingSummaryStep('Back')}
        </Button>
        <Button data-cy="validate" w="full" onClick={handleFundraisingSubmit}>
          {i18nFundraisingSummaryStep('ValidateAndPay')}
        </Button>
      </CardFooter>
    </Card>
  );
}

export type NewPrivateFundraisingSummaryProps = Props;
